import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import * as utils from '../assets/js/utils';

const reorder = (list, fromIndex, toIndex) => {
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

export default function AboutOrderSelectedDnD({
  employees,
  selectedIds,
  onSelectedChange,
  orderIds,
  onOrderChange,
  onApply,
}) {
  const [draggingId, setDraggingId] = useState(null);

  const selectedEmployees = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    const byId = new Map(employees.map(emp => [emp.id, emp]));

    return orderIds
      .filter(id => selectedSet.has(id))
      .map(id => byId.get(id))
      .filter(Boolean);
  }, [employees, selectedIds, orderIds]);

  const handleToggle = (id, checked) => {
    if (checked) {
      const nextSelected = [...selectedIds, id];
      onSelectedChange(nextSelected);
      if (!orderIds.includes(id)) onOrderChange([...orderIds, id]);
      return;
    }

    onSelectedChange(selectedIds.filter(currentId => currentId !== id));
  };

  const handleDrop = (targetId) => {
    if (!draggingId || draggingId === targetId) return;

    const fromIndex = orderIds.indexOf(draggingId);
    const toIndex = orderIds.indexOf(targetId);
    if (fromIndex === -1 || toIndex === -1) return;

    onOrderChange(reorder(orderIds, fromIndex, toIndex));
    setDraggingId(null);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Opción 2 · Selección + box drag & drop
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Marca los empleados a mostrar en About Us y ordénalos arrastrando tarjetas.
      </Typography>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Selección de empleados</Typography>
      <Box
        sx={{
          p: 1.5,
          mb: 2,
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          backgroundColor: 'background.default'
        }}
      >
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
          {employees.map(emp => (
            <FormControlLabel
              key={emp.id}
              control={(
                <Checkbox
                  checked={selectedIds.includes(emp.id)}
                  onChange={event => handleToggle(emp.id, event.target.checked)}
                />
              )}
              label={`${emp.name} ${emp.last_name || emp.lastName || ''}`.trim()}
              sx={{ mr: 0 }}
            />
          ))}
        </Stack>
      </Box>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Orden visual seleccionado</Typography>
      <Stack spacing={1.25}>
        {selectedEmployees.map((emp, index) => (
          <Box
            key={emp.id}
            draggable
            onDragStart={() => setDraggingId(emp.id)}
            onDragOver={e => e.preventDefault()}
            onDrop={() => handleDrop(emp.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.25,
              border: theme => `1px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
              backgroundColor: draggingId === emp.id ? 'action.selected' : 'background.paper',
              cursor: 'grab'
            }}
          >
            <DragIndicatorIcon color="action" />
            <Typography sx={{ width: 28, textAlign: 'center', fontWeight: 700 }}>
              {index + 1}
            </Typography>
            <Avatar src={utils.image_path(emp.image || emp.avatar)} alt={emp.name} />
            <Typography sx={{ fontWeight: 500 }}>
              {`${emp.name} ${emp.last_name || emp.lastName || ''}`.trim()}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="outlined" onClick={onApply}>
          Aplicar selección y orden a la tabla
        </Button>
      </Box>
    </Paper>
  );
}
